"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import type {
  IntegrationRequestEmailCampaign,
  IntegrationRequestEmailDraft,
} from "@/schema/integration-request-email-campaign"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@ui/shad"
import { Loader2 } from "lucide-react"

import {
  useIntegrationRequestEmailCampaigns,
  useIntegrationRequestEmailDraft,
  useMarkIntegrationRequestEmailCampaignActed,
  useMarkIntegrationRequestEmailCampaignShown,
  useSnoozeIntegrationRequestEmailCampaign,
} from "@/hooks/api/integration-request-email-campaigns"
import { GenericDialog } from "@/components/dialogs"
import { ComposeEmail } from "@/components/email/compose"

function IntegrationRequestEmailComposeDialog({
  campaignKey,
  draft,
  onActed,
}: {
  campaignKey: string
  draft: IntegrationRequestEmailDraft
  onActed: () => void
}) {
  const popDialog = useGlobalDialogStore((state) => state.popDialog)
  const actedMutation = useMarkIntegrationRequestEmailCampaignActed()

  const handleSent = () => {
    actedMutation.mutate(campaignKey, {
      onSettled: () => {
        onActed()
        popDialog()
      },
    })
  }

  return (
    <ComposeEmail
      toEmails={emailReferencesToSuggestions(draft.to)}
      ccEmails={emailReferencesToSuggestions(draft.cc)}
      startingSubject={draft.subject}
      startingHTML={draft.body_html}
      onSent={handleSent}
    />
  )
}

function emailReferencesToSuggestions(recipients: IntegrationRequestEmailDraft["to"]) {
  return recipients.map((recipient) => ({ email: recipient.email, name: recipient.name ?? null, type: null }))
}

function IntegrationRequestEmailCtaDialog({
  campaign,
  onClose,
}: {
  campaign: IntegrationRequestEmailCampaign
  onClose: () => void
}) {
  const openDialog = useGlobalDialogStore((state) => state.openDialog)
  const dismissMutation = useMarkIntegrationRequestEmailCampaignShown()
  const snoozeMutation = useSnoozeIntegrationRequestEmailCampaign()
  const draftQuery = useIntegrationRequestEmailDraft(campaign.campaign_key)
  const [loadingDraft, setLoadingDraft] = useState(false)
  const handoffRef = useRef(false)

  const openDraft = (draft: IntegrationRequestEmailDraft) => {
    handoffRef.current = true
    setLoadingDraft(false)
    openDialog({
      variant: "custom",
      content: (
        <IntegrationRequestEmailComposeDialog campaignKey={campaign.campaign_key} draft={draft} onActed={onClose} />
      ),
    })
  }
  const openDraftRef = useRef(openDraft)
  openDraftRef.current = openDraft

  useEffect(() => {
    if (loadingDraft && draftQuery.data) openDraftRef.current(draftQuery.data)
  }, [loadingDraft, draftQuery.data])

  const handleDraftEmail = () => {
    if (draftQuery.data) openDraft(draftQuery.data)
    else setLoadingDraft(true)
  }
  const isDismissing = dismissMutation.isPending || snoozeMutation.isPending

  return (
    <>
      <GenericDialog
        open
        onOpenChange={() => {}}
        dismiss="none"
        title={campaign.title}
        description={campaign.description}
        logo={
          campaign.icon_url ? <Image src={campaign.icon_url} alt="" width={40} height={40} unoptimized /> : undefined
        }
        action={{ label: campaign.draft_label, onClick: handleDraftEmail, disabled: isDismissing }}
        secondaryAction={{
          label: campaign.dismiss_label,
          onClick: () => dismissMutation.mutate(campaign.campaign_key, { onSuccess: onClose }),
          pending: dismissMutation.isPending,
          disabled: isDismissing,
        }}
      >
        <Button
          variant="link"
          size="sm"
          onClick={() => snoozeMutation.mutate(campaign.campaign_key, { onSuccess: onClose })}
          disabled={isDismissing}
        >
          Remind me later
        </Button>
      </GenericDialog>
      <GenericDialog
        open={loadingDraft}
        onOpenChange={setLoadingDraft}
        title={draftQuery.isError ? "Could not load email" : "Loading email"}
        onCloseAutoFocus={(event) => {
          if (handoffRef.current) {
            event.preventDefault()
            handoffRef.current = false
          }
        }}
        action={
          draftQuery.isError
            ? { label: "Try again", onClick: () => void draftQuery.refetch(), pending: draftQuery.isFetching }
            : undefined
        }
      >
        {!draftQuery.isError && (
          <div className="flex min-h-24 items-center justify-center">
            <Loader2 className="text-muted-foreground size-6 animate-spin" />
          </div>
        )}
      </GenericDialog>
    </>
  )
}

function IntegrationRequestEmailCampaignGate({ campaign }: { campaign: IntegrationRequestEmailCampaign | undefined }) {
  const isGlobalDialogOpen = useGlobalDialogStore((state) => state.isOpen)
  const [activeCampaign, setActiveCampaign] = useState<IntegrationRequestEmailCampaign | null>(null)

  useEffect(() => {
    if (!campaign || isGlobalDialogOpen || activeCampaign) return
    setActiveCampaign(campaign)
  }, [campaign, isGlobalDialogOpen, activeCampaign])

  return activeCampaign ? (
    <IntegrationRequestEmailCtaDialog
      key={activeCampaign.campaign_key}
      campaign={activeCampaign}
      onClose={() => setActiveCampaign(null)}
    />
  ) : null
}

export function IntegrationRequestEmailCtaGate() {
  const { data: campaigns } = useIntegrationRequestEmailCampaigns()
  const queryClient = useQueryClient()

  useEffect(() => {
    const snoozedCampaigns =
      campaigns?.filter(
        (campaign) => campaign.snoozed_until && new Date(campaign.snoozed_until).getTime() > Date.now()
      ) ?? []
    if (snoozedCampaigns.length === 0) return

    const nextSnoozedUntil = Math.min(
      ...snoozedCampaigns.map((campaign) => new Date(campaign.snoozed_until!).getTime())
    )
    const timeout = window.setTimeout(
      () => {
        queryClient.invalidateQueries({ queryKey: ["integration-request-email-campaigns"] })
      },
      Math.max(nextSnoozedUntil - Date.now(), 0)
    )

    return () => window.clearTimeout(timeout)
  }, [campaigns, queryClient])

  const campaign = campaigns?.find(
    (candidate) =>
      candidate.eligible &&
      !candidate.shown &&
      candidate.title &&
      candidate.description &&
      candidate.dismiss_label &&
      candidate.draft_label
  )

  return <IntegrationRequestEmailCampaignGate campaign={campaign} />
}
